import { Loading01 } from "@liftedcare/solidjs-untitled-icons/duocolor";
import { autofocus } from "@solid-primitives/autofocus";
import { debounce } from "@solid-primitives/scheduled";
import {
  Component,
  createEffect,
  createSignal,
  createUniqueId,
  For,
  JSX,
  JSXElement,
  Show,
} from "solid-js";

import gqlClient from "../../schema/gqlAuthorizedRequest.js";
import {
  CommandBarUserInfoFragment,
  SearchUser,
  SearchUserPropertiesType,
} from "../../schema/graphql.js";
import { graphql } from "../../schema/index.js";
import { enumToUcFirstLabel } from "../../utils/enumToLabel.js";
import { getName } from "../../utils/getName.js";

// prevent TS from tree shaking this
// eslint-disable-next-line @typescript-eslint/no-unused-expressions
autofocus;

const searchApplicantsGql = graphql(`
  query commandBarSearchApplicants(
    $name: String
    $email: String
    $phoneNumber: String
  ) {
    searchApplicants(name: $name, email: $email, phoneNumber: $phoneNumber) {
      id
      currentName {
        firstName
        lastName
      }
    }
  }
`);

const searchCandidatesGql = graphql(`
  query commandBarSearchCandidates(
    $name: String
    $email: String
    $phoneNumber: String
  ) {
    searchCandidates(name: $name, email: $email, phoneNumber: $phoneNumber) {
      id
      firstName
      lastName
    }
  }
`);

const searchStagedCandidatesGql = graphql(`
  query commandBarSearchStagedCandidates(
    $name: String
    $email: String
    $phoneNumber: String
  ) {
    searchStagedCandidates(
      name: $name
      email: $email
      phoneNumber: $phoneNumber
    ) {
      id
      firstName
      lastName
    }
  }
`);

const searchUsersGql = graphql(`
  query commandBarSearchUsers(
    $query: String!
    $properties: [SearchUserPropertiesType!]!
    $roles: [UserRoleType!]
  ) {
    searchUsers(query: $query, properties: $properties, roles: $roles) {
      onboardingUserId
      role
      firstName
      lastName
      email
    }
  }
`);

graphql(`
  fragment CommandBarUserInfo on User {
    id
    email
    phoneNumber
    role
    systemUserProfile {
      firstName
      lastName
    }
    agency {
      name
    }
  }
`);
const userInfoGql = graphql(`
  query commandBarUserInfo($onboardingUserId: Int!) {
    getUser(id: $onboardingUserId) {
      ...CommandBarUserInfo
    }
  }
`);

interface Action {
  label: string | JSX.Element;
  action: (metaKeyPressed?: boolean) => void;
}

export const UserSearch: Component<{
  actionKey: string;
  onComplete: () => void;
  onBack: () => void;
}> = (props) => {
  const [activeOptionIndex, setActiveOptionIndex] = createSignal<number>(-1);
  const [actions, setActions] = createSignal<Action[]>([]);
  const [actionsLoading, setActionsLoading] = createSignal(false);
  const [userInfo, setUserInfo] =
    createSignal<null | CommandBarUserInfoFragment>(null);

  let inputElm: HTMLInputElement;
  const scrollableContainerId = createUniqueId();

  const onKeyDown = (event: KeyboardEvent) => {
    // TODO: in the future we can have a more complex stack of actions history to go back to
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      if (userInfo()) {
        setUserInfo(null);
        return false;
      } else {
        props.onBack();
      }
    }

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        setActiveOptionIndex(
          Math.min(activeOptionIndex() + 1, actions().length - 1),
        );
        return false;
      }
      case "ArrowUp": {
        event.preventDefault();
        setActiveOptionIndex(Math.max(activeOptionIndex() - 1, 0));
        return false;
      }
      case "Enter": {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (activeOptionIndex() >= 0) {
          actions().at(activeOptionIndex())?.action(event.metaKey);
        } else if (actions().length === 1) {
          actions().at(0)!.action(event.metaKey);
        }
        return false;
      }
      // No default
    }
  };

  createEffect(() => {
    const index = activeOptionIndex();
    const container = document.getElementById(scrollableContainerId);
    const activeOption = container?.childNodes.item(index) as
      | HTMLElement
      | undefined;
    if (activeOption && container) {
      if (container.scrollTop > activeOption.offsetTop) {
        container.scrollTop = activeOption.offsetTop;
      }

      if (
        container.scrollTop +
          container.clientHeight -
          activeOption.clientHeight <=
        activeOption.offsetTop
      ) {
        container.scrollTop =
          activeOption.offsetTop +
          activeOption.clientHeight -
          container.clientHeight;
      }
    }
  });

  const placeholder = () => {
    switch (props.actionKey) {
      case "user-search-applicant": {
        return "Enter an applicant ID or name";
      }
      case "user-search-candidate": {
        return "Enter a candidate ID or name";
      }
      case "user-search-staged-candidate": {
        return "Enter a pre-screen candidate ID or name";
      }
      case "user-search-onboarding": {
        return "Enter an onboarding user ID";
      }
      case "user-search-name": {
        return "Enter a name";
      }
      case "user-search-phone": {
        return "Enter a phone number";
      }
      case "user-search-email": {
        return "Enter an email address";
      }
      default: {
        return "Enter a query";
      }
    }
  };

  const onQueryInputKeyUp = debounce(async (query: string) => {
    if (!query.length) {
      setUserInfo(null);
      return;
    }
    // check if they are searching for a user by ID
    if (
      [
        "user-search-applicant",
        "user-search-candidate",
        "user-search-staged-candidate",
      ].includes(props.actionKey) &&
      /^\d*$/.test(query)
    ) {
      let url: string;
      switch (props.actionKey) {
        case "user-search-applicant": {
          url = `/applicant/profile/${query}/compliance`;
          break;
        }
        case "user-search-candidate": {
          url = `/candidates/${query}`;
          break;
        }
        case "user-search-staged-candidate": {
          url = `/staged-candidate/${query}`;
          break;
        }
      }
      setActions([
        {
          label: "Go to profile",
          action(metaKeyPressed?: boolean) {
            window.open(url, metaKeyPressed ? Date.now().toString() : "_self");
            props.onComplete();
          },
        },
      ]);
    } else {
      // else handle other search options
      setActionsLoading(true);
      switch (props.actionKey) {
        case "user-search-applicant": {
          searchApplicants(query);
          break;
        }
        case "user-search-candidate": {
          searchCandidates(query);
          break;
        }
        case "user-search-staged-candidate": {
          searchStagedCandidates(query);
          break;
        }
        case "user-search-onboarding": {
          searchUsers({
            query,
            properties: [SearchUserPropertiesType.OnboardingUserId],
          });
          break;
        }
        case "user-search-name": {
          searchUsers({
            query,
            properties: [SearchUserPropertiesType.Name],
          });
          break;
        }
        case "user-search-email": {
          searchUsers({
            query,
            properties: [SearchUserPropertiesType.Email],
          });
          break;
        }
        case "user-search-phone": {
          searchUsers({
            query,
            properties: [SearchUserPropertiesType.Phone],
          });
          break;
        }
      }
    }
  }, 300);

  const searchApplicants = async (query: string) => {
    const response = await gqlClient.request(searchApplicantsGql, {
      name: query,
    });
    setActionsLoading(false);
    setActions(
      response.searchApplicants.map((applicant) => ({
        label: getName(applicant.currentName),
        action(metaKeyPressed?: boolean) {
          window.open(
            `/applicant/profile/${applicant.id}/compliance`,
            metaKeyPressed ? Date.now().toString() : "_self",
          );
          props.onComplete();
        },
      })),
    );
  };

  const searchCandidates = async (query: string) => {
    const response = await gqlClient.request(searchCandidatesGql, {
      name: query,
    });
    setActionsLoading(false);
    setActions(
      response.searchCandidates.map((candidate) => ({
        label: getName(candidate),
        action(metaKeyPressed?: boolean) {
          window.open(
            `/candidates/${candidate.id}`,
            metaKeyPressed ? Date.now().toString() : "_self",
          );
          props.onComplete();
        },
      })),
    );
  };

  const searchStagedCandidates = async (query: string) => {
    const response = await gqlClient.request(searchStagedCandidatesGql, {
      name: query,
    });
    setActionsLoading(false);
    setActions(
      response.searchStagedCandidates.map((stagedCandidate) => ({
        label: getName(stagedCandidate),
        action(metaKeyPressed?: boolean) {
          window.open(
            `/staged-candidate/${stagedCandidate.id}`,
            metaKeyPressed ? Date.now().toString() : "_self",
          );
          props.onComplete();
        },
      })),
    );
  };

  const searchUsers = async (query: {
    query: string;
    properties: SearchUserPropertiesType[];
  }) => {
    const response = await gqlClient.request(searchUsersGql, query);
    setActionsLoading(false);
    setActions(
      response.searchUsers.map((user) => ({
        label: (
          <p class="flex w-full justify-between">
            <span>{/*@once*/ getName(user)}</span>
            <span class="text-sm text-gray-600">
              {/*@once*/ enumToUcFirstLabel(user.role)}
            </span>
          </p>
        ),
        action() {
          selectUser(user);
        },
      })),
    );
  };

  const selectUser = (user: SearchUser) => {
    const actions = [];
    if (user.applicantId) {
      actions.push({
        label: "Go to applicant profile",
        action(metaKeyPressed?: boolean) {
          window.open(
            `/applicant/profile/${user.applicantId}/compliance`,
            metaKeyPressed ? Date.now().toString() : "_self",
          );
          props.onComplete();
        },
      });
    }
    if (user.candidateId) {
      actions.push({
        label: "Go to candidate profile",
        action(metaKeyPressed?: boolean) {
          window.open(
            `/candidates/${user.candidateId}`,
            metaKeyPressed ? Date.now().toString() : "_self",
          );
          props.onComplete();
        },
      });
    }
    if (user.stagedCandidateId) {
      actions.push({
        label: "Go to pre-screen candidate profile",
        action(metaKeyPressed?: boolean) {
          window.open(
            `/staged-candidate/${user.stagedCandidateId}`,
            metaKeyPressed ? Date.now().toString() : "_self",
          );
          props.onComplete();
        },
      });
    }
    if (!user.applicantId && !user.candidateId && !user.stagedCandidateId) {
      showUserInfo(user.onboardingUserId);
    }
    setActions(actions);
  };

  const showUserInfo = async (onboardingUserId: number) => {
    const userInfo = await gqlClient.request(userInfoGql, {
      onboardingUserId,
    });
    setUserInfo(userInfo.getUser);
  };

  const actionLabel = (label: string | JSXElement) => {
    if (typeof label !== "string") {
      return label;
    }
    return <span>{label}</span>;
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div class="space-y-1" onKeyDown={onKeyDown}>
      <input
        use:autofocus
        autofocus
        ref={inputElm!}
        placeholder={placeholder()}
        class="w-full rounded-lg border p-4 text-lg"
        onKeyUp={(e) => {
          if (
            e.key === "ArrowUp" ||
            e.key === "ArrowDown" ||
            e.key === "Enter"
          ) {
            e.preventDefault();
            return false;
          }
          onQueryInputKeyUp(e.currentTarget.value);
        }}
        type="text"
      />
      <div
        id={scrollableContainerId}
        class="relative h-[25vh] space-y-1 overflow-auto"
        style={{
          "scrollbar-color": `rgb(217 215 253) transparent`,
          "scrollbar-width": "thin",
        }}
      >
        <Show
          when={userInfo()}
          fallback={
            <>
              <For each={actions()}>
                {({ label, action }, index) => (
                  <button
                    class="w-full rounded-md p-4 text-left hover:bg-gray-100"
                    classList={{
                      "bg-primary-200 hover:bg-primary-300":
                        index() === activeOptionIndex(),
                    }}
                    onClick={(event: MouseEvent) => action(event.metaKey)}
                    onKeyPress={(e: KeyboardEvent) => {
                      if (e.key === "Enter") {
                        action(e.metaKey);
                      }
                    }}
                  >
                    <div class="flex items-center justify-between">
                      {actionLabel(label)}
                    </div>
                  </button>
                )}
              </For>
              <Show when={actions().length === 0 && !actionsLoading()}>
                <div class="p-4 text-center">No results found</div>
              </Show>
              <Show when={actionsLoading()}>
                <div class="flex justify-center p-4">
                  <Loading01 class="size-4 animate-spin" />
                </div>
              </Show>
            </>
          }
        >
          {(info) => (
            <>
              <div>
                <small class="text-gray-700">Full name</small>
                <p>{getName(info().systemUserProfile, "Not found")}</p>
              </div>
              <div>
                <small class="text-gray-700">Email</small>
                <p>{info().email ?? "Not found"}</p>
              </div>
              <div>
                <small class="text-gray-700">Phone number</small>
                <p>{info().phoneNumber ?? "Not found"}</p>
              </div>
              <div>
                <small class="text-gray-700">Agency</small>
                <p>{info().agency?.name ?? "Not found"}</p>
              </div>
            </>
          )}
        </Show>
      </div>
    </div>
  );
};
